import React, { useEffect, useState, useRef } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
    Elements,
    IbanElement,
    CardElement,
    useStripe,
    useElements,
} from "@stripe/react-stripe-js";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { CheckCircle2, Loader2, ShieldCheck, CreditCard, Landmark, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

/* ---------------------------------------------------------------------------
   PaymentMethodSetup — Bulletproof "SI on submit" flow.

   Prior version created a SetupIntent on mount and on every pmType change,
   producing up to 4 SIs per session (StrictMode + toggle) and race-condition
   "No such setupintent" errors when the stale SI got confirmed against a
   different account key after a redeploy.

   The new flow:
     1. Elements is mounted WITHOUT a clientSecret (Stripe Elements supports
        this: IbanElement / CardElement need a Stripe instance, not a SI).
     2. On submit → create a FRESH SetupIntent → immediately confirm it.
     3. This guarantees the confirmed SI belongs to the exact backend account
        currently serving the request. Zero race, zero stale SI.
   ------------------------------------------------------------------------- */

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const ELEMENT_STYLE = {
    base: {
        color: "#0B192C",
        fontFamily: '"Inter", system-ui, -apple-system, sans-serif',
        fontSize: "15px",
        "::placeholder": { color: "#94a3b8" },
        iconColor: "#F5A623",
    },
    invalid: { color: "#dc2626", iconColor: "#dc2626" },
};

const IBAN_OPTIONS = {
    supportedCountries: ["SEPA"],
    placeholderCountry: "FR",
    style: ELEMENT_STYLE,
};

const CARD_OPTIONS = { style: ELEMENT_STYLE, hidePostalCode: true };

export function PaymentMethodSetup({ onSuccess, defaultType = "sepa_debit" }) {
    const [pmType, setPmType] = useState(defaultType);
    const [health, setHealth] = useState(null);

    // One-time backend account check — helps diagnose mismatched keys.
    useEffect(() => {
        api.get("/stripe/health")
            .then(({ data }) => setHealth(data))
            .catch(() => setHealth({ ok: false, error: "unreachable" }));
    }, []);

    return (
        <div
            className="sd-card p-6 mb-6 border-2 border-[#0B192C]/10"
            data-testid="payment-method-setup"
        >
            <div className="flex items-center gap-2 mb-1">
                <ShieldCheck className="text-emerald-600" size={16} />
                <div className="overline">Étape sécurité · paiement différé J+10</div>
            </div>
            <h3 className="font-display text-lg mb-2">Enregistrez votre moyen de paiement</h3>
            <p className="text-xs text-slate-500 mb-4 leading-relaxed">
                Aucun débit maintenant. Ce mandat autorise Renforteo à prélever
                automatiquement le montant total du cycle (missions regroupées tous
                les 10 jours) sur votre compte, une fois la période clôturée.
            </p>

            {/* Type selector */}
            <div className="grid grid-cols-2 gap-2 mb-5">
                <button
                    type="button"
                    onClick={() => setPmType("sepa_debit")}
                    className={`rounded-xl border-2 p-3 text-left transition-colors ${
                        pmType === "sepa_debit"
                            ? "border-[#F5A623] bg-[#F5A623]/5"
                            : "border-slate-200 bg-white hover:border-slate-300"
                    }`}
                    data-testid="pm-type-sepa"
                >
                    <div className="flex items-center gap-2">
                        <Landmark size={16} className="text-[#0B192C]" />
                        <div className="text-sm font-semibold text-[#0B192C]">SEPA (IBAN)</div>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1">
                        Recommandé B2B · frais ~0,25 €
                    </div>
                </button>
                <button
                    type="button"
                    onClick={() => setPmType("card")}
                    className={`rounded-xl border-2 p-3 text-left transition-colors ${
                        pmType === "card"
                            ? "border-[#F5A623] bg-[#F5A623]/5"
                            : "border-slate-200 bg-white hover:border-slate-300"
                    }`}
                    data-testid="pm-type-card"
                >
                    <div className="flex items-center gap-2">
                        <CreditCard size={16} className="text-[#0B192C]" />
                        <div className="text-sm font-semibold text-[#0B192C]">Carte bancaire</div>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1">
                        Fallback · 1,5 % par débit
                    </div>
                </button>
            </div>

            <Elements
                stripe={stripePromise}
                options={{
                    locale: "fr",
                    appearance: {
                        theme: "stripe",
                        variables: {
                            colorPrimary: "#F5A623",
                            colorText: "#0B192C",
                            borderRadius: "12px",
                            fontFamily: '"Inter", system-ui, sans-serif',
                        },
                    },
                }}
            >
                <PaymentMethodForm
                    pmType={pmType}
                    onSuccess={onSuccess}
                    health={health}
                />
            </Elements>
        </div>
    );
}

function PaymentMethodForm({ pmType, onSuccess, health }) {
    const stripe = useStripe();
    const elements = useElements();
    const { user } = useAuth();
    const [submitting, setSubmitting] = useState(false);
    const [mandateAccepted, setMandateAccepted] = useState(false);
    const [holderName, setHolderName] = useState(user?.company_name || user?.full_name || "");
    const [holderEmail, setHolderEmail] = useState(user?.email || "");
    // Prevent double-clicks
    const inFlightRef = useRef(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!stripe || !elements) return;
        if (inFlightRef.current) return;

        // Client-side validation
        if (!holderName || !holderEmail) {
            toast.error("Merci de remplir le nom et l'email du titulaire");
            return;
        }
        if (pmType === "sepa_debit" && !mandateAccepted) {
            toast.error("Merci d'accepter le mandat SEPA");
            return;
        }

        inFlightRef.current = true;
        setSubmitting(true);

        try {
            // -------------------------------------------------------------
            // STEP 1 — Create a FRESH SetupIntent on the server (always
            //          matches the current backend Stripe account).
            // -------------------------------------------------------------
            const siResp = await api.post(
                "/company/payment-methods/setup-intent",
                { type: pmType },
            );
            const { client_secret: clientSecret, setup_intent_id: sid } = siResp.data;
            if (!clientSecret || !sid) {
                toast.error("Impossible de préparer le paiement — recharger la page");
                return;
            }
            // eslint-disable-next-line no-console
            console.log("[Renforteo] fresh SI created:", sid, "account:", health?.account_id);

            // -------------------------------------------------------------
            // STEP 2 — Confirm the SI with the collected IBAN / card.
            // -------------------------------------------------------------
            let result;
            if (pmType === "sepa_debit") {
                const iban = elements.getElement(IbanElement);
                if (!iban) {
                    toast.error("Champ IBAN non chargé — recharger la page");
                    return;
                }
                result = await stripe.confirmSepaDebitSetup(clientSecret, {
                    payment_method: {
                        sepa_debit: iban,
                        billing_details: { name: holderName, email: holderEmail },
                    },
                });
            } else {
                const card = elements.getElement(CardElement);
                if (!card) {
                    toast.error("Champ carte non chargé — recharger la page");
                    return;
                }
                result = await stripe.confirmCardSetup(clientSecret, {
                    payment_method: {
                        card,
                        billing_details: { name: holderName, email: holderEmail },
                    },
                });
            }
            // eslint-disable-next-line no-console
            console.log("[Renforteo] confirmSetup result:", result);

            if (result.error) {
                console.error("[Renforteo] Stripe error:", result.error);
                const msg = result.error.message || result.error.code || "";
                // Special case: mismatched publishable / secret keys
                if (/No such setupintent/i.test(msg)) {
                    toast.error(
                        "Configuration Stripe incompatible entre le front et le back " +
                        "(clé publishable et clé secret sur des comptes différents). " +
                        "Contactez le support Renforteo.",
                        { duration: 15000 },
                    );
                } else {
                    toast.error(msg || "Échec de l'enregistrement — vérifiez les infos saisies");
                }
                return;
            }
            if (!result.setupIntent) {
                toast.error("Réponse Stripe invalide — contactez le support");
                return;
            }

            // -------------------------------------------------------------
            // STEP 3 — Persist server-side.
            // -------------------------------------------------------------
            const { data } = await api.post("/company/payment-methods/save", {
                setup_intent_id: result.setupIntent.id,
                make_default: true,
            });

            toast.success(
                data.type === "sepa_debit"
                    ? `Mandat SEPA enregistré · IBAN ····${data.last4}`
                    : `Carte enregistrée · ${data.brand || "CB"} ····${data.last4}`,
            );

            // Reset the collected data so a subsequent add starts clean
            if (pmType === "sepa_debit") elements.getElement(IbanElement)?.clear();
            else elements.getElement(CardElement)?.clear();
            setMandateAccepted(false);

            onSuccess && onSuccess(data);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error("[Renforteo] submit err:", err);
            toast.error(
                err?.response?.data?.detail ||
                err?.message ||
                "Erreur lors de l'enregistrement",
            );
        } finally {
            inFlightRef.current = false;
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            {/* Optional key mismatch banner */}
            {health && health.ok === false && (
                <div
                    className="flex gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800"
                    data-testid="stripe-health-warning"
                >
                    <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                    <span>
                        Impossible de vérifier la configuration Stripe côté serveur.
                        L'enregistrement peut échouer — contactez le support si le
                        problème persiste.
                    </span>
                </div>
            )}

            {/* Billing holder info */}
            <div className="grid sm:grid-cols-2 gap-3">
                <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">
                        Titulaire du compte
                    </label>
                    <input
                        type="text"
                        value={holderName}
                        onChange={(e) => setHolderName(e.target.value)}
                        placeholder="Nom / Raison sociale"
                        className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#F5A623]"
                        data-testid="pm-holder-name"
                        required
                    />
                </div>
                <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">
                        Email de facturation
                    </label>
                    <input
                        type="email"
                        value={holderEmail}
                        onChange={(e) => setHolderEmail(e.target.value)}
                        placeholder="facturation@entreprise.fr"
                        className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#F5A623]"
                        data-testid="pm-holder-email"
                        required
                    />
                </div>
            </div>

            {/* IBAN or Card — both stay mounted (Elements only unmounts the
                inactive one) so switching type keeps the entered value on the
                other type intact until submit. */}
            {pmType === "sepa_debit" ? (
                <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">
                        IBAN (SEPA)
                    </label>
                    <div
                        className="border border-slate-300 rounded-lg px-3 py-3 bg-white focus-within:border-[#F5A623]"
                        data-testid="pm-iban-wrapper"
                    >
                        <IbanElement options={IBAN_OPTIONS} />
                    </div>
                    <p className="text-[11px] text-slate-400 mt-1">
                        Format attendu : FR76 xxxx xxxx xxxx xxxx xxxx xxx
                    </p>
                </div>
            ) : (
                <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">
                        Carte bancaire
                    </label>
                    <div
                        className="border border-slate-300 rounded-lg px-3 py-3 bg-white focus-within:border-[#F5A623]"
                        data-testid="pm-card-wrapper"
                    >
                        <CardElement options={CARD_OPTIONS} />
                    </div>
                </div>
            )}

            {pmType === "sepa_debit" && (
                <label className="flex items-start gap-2 text-xs text-slate-600 leading-relaxed">
                    <input
                        type="checkbox"
                        checked={mandateAccepted}
                        onChange={(e) => setMandateAccepted(e.target.checked)}
                        className="mt-0.5 accent-[#F5A623]"
                        data-testid="sepa-mandate-checkbox"
                    />
                    <span>
                        J'autorise <b>Renforteo</b> à envoyer des instructions à ma banque
                        pour débiter mon compte, et ma banque à débiter mon compte
                        conformément aux instructions de Renforteo. Prélèvements récurrents
                        tous les 10 jours à la clôture du cycle de facturation.
                    </span>
                </label>
            )}

            <button
                type="submit"
                disabled={!stripe || submitting}
                className="btn-gold w-full py-3 text-sm"
                data-testid="submit-payment-method-btn"
            >
                {submitting ? (
                    <Loader2 className="inline animate-spin" size={16} />
                ) : (
                    <>
                        <CheckCircle2 className="inline -mt-1 mr-1" size={16} />
                        Enregistrer et continuer
                    </>
                )}
            </button>
        </form>
    );
}

export default PaymentMethodSetup;
